/**
 * Pre-checkout integrity guard for configurator bundles — loaded globally
 * from layout/theme.liquid because a broken bundle can sit in the cart on
 * any page. Sweeps the cart on load and after every cart change; if a bundle
 * failed its transform (container never expanded, or a component line rings
 * up at €0), enforceCartIntegrity removes it server-side before the customer
 * can reach checkout with a free setup, and stale `_bundle_<n>_*` audit
 * attributes of a removed bundle are nulled so they never ride onto an order
 * (order #1023, 2026-09-02, shipped a full bundle trio on a voucher-only
 * order because this sweep never ran after the removal).
 *
 * Client-side net only: anyone bypassing it is the order-create validator's
 * problem (cart-contract §4/§10). The guard must never break the page — every
 * path swallows its own errors.
 *
 * Trigger design (cart-contract §10):
 *   (a) page load, when the server-rendered flag (snippets/bundle-cart-state
 *       .liquid) says the cart carries bundle state, or the header bubble
 *       shows lines;
 *   (b) every `cartUpdate` pub/sub event — registration is retried on
 *       DOMContentLoaded and load because deferred module scripts run in
 *       document order at readyState "interactive", i.e. possibly BEFORE
 *       pubsub.js has defined `subscribe` (that ordering silently disabled
 *       this path until 2026-09-02);
 *   (b2) the standard `shopify:cart:lines-update` event, which Dawn fires
 *       before the part of its .then chain that can throw and skip (b);
 *   (c) a `cartUpdate` arriving DURING a sweep re-runs it once afterwards
 *       instead of being dropped (the in-flight /cart.js read predates the
 *       change that fired the event);
 *   (d) a checkout submit while a sweep is in flight waits for it (capped),
 *       so /cart/update.js lands before the checkout POST — through the
 *       theme's single checkout gate (assets/checkout-gate.js).
 *
 * Entry point only — nothing imports this file — so the exports below exist
 * purely for tests/bundle-cart-guard.test.mjs. Everything with a side effect
 * lives in boot(), which runs only in a browser.
 */
import { enforceCartIntegrity } from '#bundle-cart';
import { installCheckoutGate, registerCheckoutWaiter, isCheckoutSubmit, awaitSweep } from '#checkout-gate';

// Kept as exports for tests/bundle-cart-guard.test.mjs; the implementation
// lives in assets/checkout-gate.js since 2026-09-13, where the cart mounting
// writer registers as a second waiter on the same held click.
export { isCheckoutSubmit, awaitSweep };

/**
 * Reads the server-rendered snapshot from snippets/bundle-cart-state.liquid.
 * Missing tag or broken JSON → {} so shouldSweepOnLoad falls back to the
 * header-bubble heuristic.
 */
export function readBundleCartState(doc) {
  try {
    const el = doc.querySelector('script[data-bundle-cart-state]');
    if (!el) return {};
    return JSON.parse(el.textContent) || {};
  } catch {
    return {};
  }
}

/**
 * Pure. OR on purpose: `hasBundleState` alone would be exact and skip a
 * /cart.js on most pageviews, but the bubble heuristic is what shipped and
 * we do not want to be worse than today anywhere. What is NEW is the empty
 * cart with dead attributes — the bubble is gone, the flag is not.
 */
export function shouldSweepOnLoad({ hasBundleState, hasCartLines }) {
  return hasBundleState === true || hasCartLines === true;
}

/**
 * Builds the sweeper. Injectable so the re-entrancy contract is testable
 * without DOM or fetch.
 *
 * A cartUpdate arriving while a sweep runs sets `pending`; the loop then
 * re-runs exactly once (several events collapse into one re-run). `maxCycles`
 * caps a theoretical ping-pong with foreign cartUpdate publishers. `onCleaned`
 * returning false ends the loop (the reload path). `removedLines` answers
 * "did THIS sweep remove a line" and resets when the next sweep starts.
 */
export function createSweeper({ enforce, onCleaned = async () => true, maxCycles = 3 }) {
  let sweeping = false;
  let pending = false;
  let inFlight = null;
  let removedLines = false;

  function sweep() {
    if (sweeping) {
      pending = true;
      return inFlight;
    }
    sweeping = true;
    removedLines = false; // per sweep — the gate asks about THIS sweep
    inFlight = (async () => {
      try {
        let cycles = 0;
        do {
          pending = false;
          const { cleaned, cart } = await enforce();
          if (cleaned) {
            removedLines = true;
            if ((await onCleaned(cart)) === false) return;
          }
        } while (pending && ++cycles < maxCycles);
      } catch {
        /* never break the page */
      } finally {
        sweeping = false;
        pending = false;
      }
    })();
    return inFlight;
  }

  return {
    sweep,
    get sweeping() { return sweeping; },
    get inFlight() { return inFlight; },
    get removedLines() { return removedLines; },
  };
}

// ---------------------------------------------------------------------------
// Browser wiring. Nothing below runs under node --test.
// ---------------------------------------------------------------------------

// Mirrors cart.js's section-render pattern for the header bubble, so the
// count doesn't advertise the removed line until the next navigation.
async function refreshCartIconBubble(cartUrl) {
  try {
    const response = await fetch(`${cartUrl}?sections=cart-icon-bubble`);
    if (!response.ok) return;
    const sections = await response.json();
    const bubble = document.getElementById('cart-icon-bubble');
    const html = sections['cart-icon-bubble'];
    if (!bubble || !html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    bubble.innerHTML = parsed.querySelector('.shopify-section')?.innerHTML ?? bubble.innerHTML;
  } catch {
    /* cosmetic only */
  }
}

function boot() {
  const sweeper = createSweeper({
    enforce: enforceCartIntegrity,
    onCleaned: async (cart) => {
      const cartUrl = window.routes?.cart_url || '/cart';
      if (location.pathname === cartUrl) {
        // main-cart is server-rendered — a reload is the one honest refresh.
        location.reload();
        return false;
      }
      await refreshCartIconBubble(cartUrl);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-cart-guard', cartData: cart });
      }
      return true;
    },
  });

  let subscribed = false;
  function tryRegisterCartUpdate() {
    if (subscribed) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'bundle-cart-guard') return;
      sweeper.sweep();
    });
    subscribed = true;
  }
  // The script tag sits after pubsub.js, so the first attempt normally
  // succeeds; the two retries exist so a future reorder of theme.liquid
  // cannot silently disable path (b) again.
  tryRegisterCartUpdate();
  document.addEventListener('DOMContentLoaded', tryRegisterCartUpdate, { once: true });
  window.addEventListener('load', tryRegisterCartUpdate, { once: true });

  // Second, independent trigger: Dawn's CartItems dispatch the standard
  // `shopify:cart:lines-update` event (bubbles) BEFORE the fragile tail of
  // their .then chain and resolve its promise as soon as /cart/change has
  // answered. The pub/sub publish sits AFTER that tail, and one null focus
  // target was enough to make the whole chain fall into .catch and skip it
  // (observed 2026-09-02: remove one of two sets). Sweeping off the standard
  // event keeps the guard alive even if that chain breaks again; the
  // sweeper collapses the duplicate into one re-run.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const settled = event?.promise ? Promise.resolve(event.promise).catch(() => {}) : Promise.resolve();
    settled.then(() => sweeper.sweep());
  });

  // (d) the shared checkout gate holds a checkout click while a sweep is in
  // flight (≤ 1500 ms) and re-submits the live form unless the sweep removed
  // a line — then the customer sees the corrected cart and clicks again.
  installCheckoutGate();
  registerCheckoutWaiter({
    busy: () => sweeper.sweeping,
    inFlight: () => sweeper.inFlight,
    changedCart: () => sweeper.removedLines,
  });

  const { hasBundleState } = readBundleCartState(document);
  const hasCartLines = !!document.getElementById('cart-icon-bubble')?.querySelector('.cart-count-bubble');
  if (shouldSweepOnLoad({ hasBundleState, hasCartLines })) sweeper.sweep();
}

if (typeof document !== 'undefined') boot();
